<template>
    <div class="d-flex h-100">
        <div class="sidebar-container" :class="{ 'sidebar-container-open': showSidebar }">
            <button
                v-for="group in Object.keys(systemTemplates)"
                :key="group"
                @click="
                    () => {
                        currentMenu = group;
                        showSidebar = true;
                    }
                "
                class="sidebar-menu-button"
                :class="{ 'sidebar-menu-button-active': currentMenu === group && showSidebar }"
            >
                <font-awesome-icon size="lg" :icon="['fas', getIcon(group)]" />
                <span>{{ getLabel(group) }}</span>
            </button>
            <button
                @click="toggleSidebar"
                class="floating-arrow-button"
                :class="{ 'floating-arrow-button-active': showSidebar }"
            >
                <font-awesome-icon size="lg" :icon="['fas', showSidebar ? 'chevron-left' : 'chevron-right']" />
            </button>
        </div>
        <transition>
            <div v-if="showSidebar && currentMenu" id="templates-sidebar-container" class="sidebar-inner-container">
                <div class="sidebar-inner-content pt-4 pl-3 pr-3">
                    <div
                        class="sidebar-content-item"
                        v-for="type in Object.keys(systemTemplates[currentMenu] || {})"
                        :key="type"
                        @click="collapse[type] = !collapse[type]"
                    >
                        <div class="d-flex align-items-center justify-content-between cursor-pointer">
                            <p class="font-weight-bold mb-0">{{ getLabel(type) }}</p>
                            <font-awesome-icon :icon="['fas', collapse[type] ? 'chevron-down' : 'chevron-right']" />
                        </div>
                        <transition name="slide">
                            <div v-if="collapse[type]" class="overflow-auto">
                                <div class="d-flex flex-column pt-2 pl-4 pr-1">
                                    <div
                                        v-for="templateData in systemTemplates[currentMenu][type]"
                                        :key="templateData.id"
                                    >
                                        <div
                                            class="sidebar-inner-submenu-item"
                                            @click.stop="selectTemplate(templateData)"
                                            :class="{
                                                'sidebar-inner-submenu-item-active': templateData.id === template.id,
                                            }"
                                        >
                                            <span>{{ templateData.name }}</span>
                                            <font-awesome-icon
                                                v-if="showDefaultIcon(templateData)"
                                                :icon="['fas', 'star']"
                                                class="ml-2 text-primary"
                                                title="Default Template"
                                            />
                                            <font-awesome-icon
                                                v-if="templateData.require_validation"
                                                :icon="['fas', 'exclamation-triangle']"
                                                class="ml-2 text-warning"
                                                title="Requires Validation"
                                            />
                                            <font-awesome-icon
                                                v-if="templateData.use_as_pdf"
                                                :icon="['far', 'file-pdf']"
                                                class="ml-2 text-primary"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </transition>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { getTemplateTypeLabel, TEMPLATE_GROUPS } from './constants';
export default {
    name: 'UnlayerTemplatesSidebar',
    props: {
        systemTemplates: {
            type: Object,
            required: true,
        },
        template: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            currentMenu: this.template.template_group,
            showSidebar: true,
            currentTemplateType: this.template.template_type,
            collapse: {},
        };
    },
    methods: {
        getLabel(type) {
            if (type === TEMPLATE_GROUPS.MISCELLANEOUS) {
                return 'Misc';
            }
            return getTemplateTypeLabel(type);
        },
        selectTemplate(templateData) {
            this.$emit('selectTemplate', templateData);
        },
        toggleSidebar() {
            this.showSidebar = !this.showSidebar;
        },
        showDefaultIcon(templateData) {
            return templateData.is_default_rental_agreement;
        },
        getIcon(group) {
            switch (group) {
                case TEMPLATE_GROUPS.OWNER:
                    return 'users';
                case TEMPLATE_GROUPS.GUEST:
                    return 'user';
                case TEMPLATE_GROUPS.CUSTOM:
                    return 'file-alt';
                case TEMPLATE_GROUPS.OTHER:
                    return 'clipboard-list';
                case TEMPLATE_GROUPS.HOST:
                    return 'building';
                default:
                    return 'cog';
            }
        },
    },
    watch: {
        template(newVal) {
            this.currentMenu = newVal.template_group;
            this.currentTemplateType = newVal.template_type;
        },
        systemTemplates(newVal) {
            if (!Object.keys(this.collapse).length) {
                this.collapse = Object.keys(newVal).reduce((acc, group) => {
                    Object.keys(newVal[group] || {}).forEach((type) => {
                        acc[type] = true;
                    });
                    return acc;
                }, {});
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.sidebar-container {
    width: 60px;
    border-right: 1px solid rgb(212, 212, 212);
    background: rgb(12, 72, 161);
    position: relative;
    transition: all 0.5s ease-in-out;
    border-top: 1px solid #e0e0e0;
    border-bottom: 1px solid #e0e0e0;
    &-open {
        border-right: none;
    }
}

.sidebar-menu-button {
    width: 100%;
    padding: 10px;
    transition: all 0.2s ease-in-out;
    border: none;
    outline: none;
    box-shadow: none;
    border-bottom: 1px solid #e0e0e0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3px;
    color: #fff;
    background: rgb(12, 72, 161);
    &:hover,
    &-active {
        background: rgb(249, 249, 249);
        color: #111;
    }
    span {
        font-size: 12px;
    }
}

.sidebar-inner-container {
    width: 280px;
    height: 100%;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-left: none;
    overflow-y: auto;
    background: rgb(249, 249, 249);
}

.floating-arrow-button {
    position: absolute;
    z-index: 10;
    top: 40%;
    right: -15px;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-left: none;
    border-radius: 100%;
    padding: 5px;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.5s ease-in-out;
    &:hover,
    &-active {
        background: rgb(12, 72, 161);
        color: #fff;
    }

    &-active {
        transform: translateX(280px);
    }
}

.v-enter-active,
.v-leave-active {
    transition: width 0.5s ease-in-out;
}

.v-enter-from,
.v-leave-to {
    width: 0;
}

.v-enter-to,
.v-leave-from {
    width: 280px;
}

.slide-enter-active,
.slide-leave-active {
    transition: max-height 0.5s ease-in-out;
}
.slide-enter-from,
.slide-leave-to {
    max-height: 0;
}
.slide-enter-to,
.slide-leave-from {
    max-height: 280px;
}

.sidebar-inner-content {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.sidebar-content-item {
    padding: 10px;
    border-radius: 5px;
    background: #fff;
    border: 1px solid #e0e0e0;
    min-width: 150px;
}

.sidebar-inner-submenu-item {
    background: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 10px 0;
    &:hover,
    &-active {
        background-color: rgba(12, 72, 161, 0.1);
        border-left: 3px solid rgb(12, 72, 161);
        font-weight: 500;
        color: rgb(12, 72, 161);
        padding-left: 7px;
        transition: all 0.2s ease-in-out;
        box-shadow: 0 2px 5px rgba(12, 72, 161, 0.15);
    }
}
</style>
